import {
	Grid,
	LoadingOverlay,
	Modal,
	NumberInput,
	Space,
	TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
	IconArrowDown,
	IconArrowUp,
	IconBuilding,
	IconCalculator,
	IconClipboardText,
} from '@tabler/icons-react';
import type { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { useUpsertBranchStockForm } from '../hooks/useUpsertBranchStockForm';
import type {
	BranchStockDto,
	UpsertBranchStockDto,
} from '../types/BranchStockDto';

interface EditBarcodeModalProps {
	close: () => void;
	isOpened: boolean;
	updateProductRecord: Dispatch<SetStateAction<BranchStockDto>>;
	skuid: string;
	branchName: string;
	branchId: string;
	defaultBranchStock: UpsertBranchStockDto;
}

export function EditBranchStockModal({
	isOpened,
	close,
	updateProductRecord,
	defaultBranchStock,
	skuid,
	branchName,
	branchId,
}: EditBarcodeModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertBranchStockForm(skuid, defaultBranchStock);

	const editBranchStockMutation = mutate<UpsertBranchStockDto, BranchStockDto>(
		`${organisation}/branch-stock/edit/${branchId}`,
		inputs,
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		resetAndClose();

		if (editBranchStockMutation.data) {
			form.setInitialValues(inputs);
			updateProductRecord(editBranchStockMutation.data.value);
		}
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Branch Stock'} />}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={editBranchStockMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Branch Stock Edited!'}
					body={
						'A branch stock has been successfully edited. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await editBranchStockMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<Grid>
						<Grid.Col span={6}>
							<TextInput
								label={'SKUID'}
								leftSection={<IconClipboardText {...defaultIconStyle} />}
								value={skuid}
								readOnly
								variant="filled"
							/>
						</Grid.Col>
						<Grid.Col span={6}>
							<TextInput
								label={'Branch'}
								leftSection={<IconBuilding {...defaultIconStyle} />}
								value={branchName}
								readOnly
								variant="filled"
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							<TextInput
								label={'Description'}
								placeholder={'Insert description...'}
								leftSection={<IconClipboardText {...defaultIconStyle} />}
								key={form.key('description')}
								{...form.getInputProps('description')}
							/>
						</Grid.Col>

						<Grid.Col span={4}>
							<NumberInput
								label={'Current Stock'}
								placeholder={'Insert current stock...'}
								key={form.key('stock')}
								{...form.getInputProps('stock')}
								leftSection={<IconCalculator {...defaultIconStyle} />}
								allowDecimal={false}
								thousandSeparator={','}
							/>
						</Grid.Col>
						<Grid.Col span={4}>
							<NumberInput
								label={'Minimum Stock'}
								placeholder={'Insert minimum stock...'}
								key={form.key('minStock')}
								{...form.getInputProps('minStock')}
								leftSection={<IconArrowDown {...defaultIconStyle} />}
								allowDecimal={false}
								thousandSeparator={','}
							/>
						</Grid.Col>
						<Grid.Col span={4}>
							<NumberInput
								label={'Maximum Stock'}
								placeholder={'Insert maximum stock...'}
								key={form.key('maxStock')}
								{...form.getInputProps('maxStock')}
								leftSection={<IconArrowUp {...defaultIconStyle} />}
								allowDecimal={false}
								thousandSeparator={','}
							/>
						</Grid.Col>
					</Grid>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
